import * as IoC from 'typescript-ioc';
import * as mobx from 'mobx';
import { addMinutes, isBefore, isSameDay, isValid } from 'date-fns';
import { format, zonedTimeToUtc } from 'date-fns-tz';
import { IntlShape } from 'react-intl';
import { Access, ILinkDTO, IMeetingSaveDTO, IMeetingsDAO, MeetingStatus } from '../../../../../api/meetings.dao';
import { IEventRequestParams, IWithExternalIdEventRequestParams } from '../../../../../api/shared.dto';
import { utcToZonedTime } from '../../../../../infrastructure/utils/date-fns-overrides.utils';
import { DateFnsFormats, TimeFormats } from '../../../../../infrastructure/date-formatter';
import { getShortRichTextLengthError, getShortTextLengthError } from '../../../../../infrastructure/utils/form-validation.utils';
import UploadImageS3VM from '../../shared/upload-image/upload-image-to-s3.viewmodel';
import { Permission } from '../../../../../api/roles.dao';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import { MeetingFormTemplatesVM } from './meeting-form-templates.viewmodel';
import { IMeetingTemplateDTO, MeetingTemplateLink } from '../../../../../api/blueprints/meeting.template.types';
import { IBlueprintItemDetails } from '../../../../../api/blueprints/blueprint-details.types';
import { convertBlueprintTemplateIntoBlueprintDetails } from '../../../../../infrastructure/utils/blueprint-details.mapper';
import { GlobalViewModel } from '../../../../global.viewmodel';
import { BlueprintType } from '../../../../../api/blueprints/blueprint.types';
import { IReactIntlProvider } from '../../../../../infrastructure/react-intl-provider';
import { ConnectionVersion } from '../../../../../constants/common.constants';
import { transpileClassesToInlineStyles, transpileInlineStylesToClasses } from '../../../../global/rich-text-editor/utils/transpiler';
import { MeetingExpiryDateVM } from './_meeting-expiry-date/meeting-expiry-date.viewmodel';
import { MeetingGoLiveDateVM } from './_meeting-go-live-date/meeting-go-live-date.viewmodel';

// Any manipulations on data items shall be performed here
// if something can not be set directly - make it private with public getter
interface IMeetingDateTime {
    meetingDate: Date;
    startDate: Date;
    endDate: Date;
    meetingDatesChanged?: boolean;
    startTimeValidationError?: boolean;
    endTimeValidationError?: boolean;
    isFormChecked?: boolean;
}

const defaultValues: IMeetingSaveDTO = {
    externalId: '',
    room: null,
    topic: '',
    status: MeetingStatus.Scheduled,
    presenters: [],
    hosts: [],
    startDateTime: null,
    endDateTime: null,
    participants: [],
    registrants: [],
    supports: [],
    notes: '',
    type: '',
    track: '',
    path: null,
    passcode: null,
    meetingLink: '',
    meetingLinkWithEmbeddedBypassCode: '',
    meetingAccess: Access.Closed,
    meetingRecordEnabled: false,
    thumbnailImage: {
        title: '',
        objectKey: ''
    },
    thumbnailImageWithUrl: {
        title: '',
        assetUrl: ''
    },
    heroImage: {
        title: '',
        objectKey: ''
    },
    heroImageWithUrl: {
        title: '',
        assetUrl: ''
    },
    featured: false,
    replayEnabled: false,
    audienceCapacityEnabled: false,
    audienceCapacity: null,
    links: [],
    blueprintId: null,
    expiryDays: null,
    goLiveDateTime: null
};

// error messages
const TEXT_DATE_REQUIRED_ERROR_MESSAGE = {
    id: 'oe.meeting.dateIsRequiredField',
    defaultMessage: 'Meeting date is required field',
    description: 'Validation error message'
};

const TEXT_END_DATE_REQUIRED_ERROR_MESSAGE = {
    id: 'oe.meeting.endDateIsRequiredField',
    defaultMessage: 'Meeting end date is required field',
    description: 'Validation error message'
};

const TEXT_EXTERNAL_ID_ERROR_MESSAGE = {
    id: 'oe.meeting.externalIDMustBeUniqueWithinTheEvent',
    defaultMessage: 'External ID must be unique within the Event',
    description: 'Validation error message'
};

const TEXT_START_END_TIME_REQUIRED_ERROR_MESSAGE = {
    id: 'oe.meeting.startTimeAndEndTimeAreRequiredFields',
    defaultMessage: 'Start Time and End Time are required fields',
    description: 'Validation error message'
};

const TEXT_START_END_TIME_EQUAL_GREATER_ERROR_MESSAGE = {
    id: 'oe.meeting.startTimeCannotBeEqualOrGreaterThanEndTime',
    defaultMessage: 'Start time cannot be equal or greater than End time',
    description: 'Validation error message'
};

const TEXT_START_END_DATE_GREATER_ERROR_MESSAGE = {
    id: 'oe.meeting.startDateCannotBeGreaterThanEndTime',
    defaultMessage: 'Start date cannot be greater than End date',
    description: 'Validation error message'
};

const TEXT_TIME_INVALID_ERROR_MESSAGE = {
    id: 'oe.meeting.invalidStartTimeOrEndTime',
    defaultMessage: 'Invalid Start Time or End Time',
    description: 'Validation error message'
};

const TEXT_START_TIME_INVALID_ERROR_MESSAGE = {
    id: 'oe.meeting.invalidStartTime',
    defaultMessage: 'Invalid Start Time',
    description: 'Validation error message'
};

const TEXT_END_TIME_INVALID_ERROR_MESSAGE = {
    id: 'oe.meeting.invalidEndTime',
    defaultMessage: 'Invalid End Time',
    description: 'Validation error message'
};

const TEXT_AUDIENCE_CAPACITY_RANGE_ERROR_MESSAGE = {
    id: 'oe.meeting.audienceCapacityRangeError',
    defaultMessage: 'Meeting capacity should be a whole number between 1 and 99999',
    description: 'Validation error message'
};

const fieldNames = {
    topic: 'Topic',
    type: 'Type',
    track: 'Track',
    externalId: 'External ID',
};

const defaultParams = {
    eventId: null,
    accountId: null
};

class MeetingFormDataVM {
    @mobx.observable
    public topic: string;
    @mobx.observable
    public room: {
        roomId: number;
        roomName: string;
        roomLabel: string;
    };
    @mobx.observable
    public meetingDate: Date;
    @mobx.observable
    public meetingEndDate: Date = null;
    @mobx.observable
    public showEndDate: boolean = false;
    @mobx.observable
    public startDate: Date;
    @mobx.observable
    public endDate: Date;
    @mobx.observable
    public meetingDatesChanged: boolean;
    @mobx.observable
    public type: string;
    @mobx.observable
    public track: string;
    @mobx.observable
    public notes: string;
    @mobx.observable
    public status: MeetingStatus;
    @mobx.observable
    public access: Access;
    @mobx.observable
    public isFormChecked: boolean;
    public path: string;
    public passcode: string;
    public meetingLink: string;
    public meetingLinkWithEmbeddedBypassCode: string;
    @mobx.observable
    public dateTimeList: IMeetingDateTime[];
    public sameStartEndDate: boolean = false;
    @mobx.observable
    public uploadThumbnailImageS3VM: UploadImageS3VM = null;
    @mobx.observable
    public uploadHeroImageS3VM: UploadImageS3VM = null;
    @mobx.observable
    public featured = false;
    @mobx.observable
    public replayEnabled = false;
    @mobx.observable
    public isAudienceCapacityEnabled = false;
    @mobx.observable
    public audienceCapacity = '';
    @mobx.observable
    public meetingLinkClick = false;
    @mobx.observable
    public meetingLinkWithEmbeddedBypassCodeClick = false;
    @mobx.observable
    public meetingStartTimeChanged: boolean = false;
    @mobx.observable
    public meetingEndTimeChanged: boolean = false;
    @mobx.observable
    public meetingRecordEnabled: boolean = false;
    @mobx.observable
    public links: ILinkDTO[];
    @mobx.observable
    public addedLinks: ILinkDTO[];
    @mobx.observable
    public templatesVM: MeetingFormTemplatesVM;
    @mobx.observable
    public expiryVM: MeetingExpiryDateVM;
    @mobx.observable
    public goLiveVM: MeetingGoLiveDateVM;
    @mobx.observable
    private startTimeValidationError: boolean = false;
    @mobx.observable
    private endTimeValidationError: boolean = false;
    private meetingId: number;
    private params: IEventRequestParams;
    @mobx.observable
    private externalId: string;
    @mobx.observable
    private externalIdValidationError: boolean = false;
    @mobx.observable
    private connectionVersion: boolean;
    private expiryVMReactionDisposers: mobx.IReactionDisposer[] = [];
    private goLiveVMReactionDisposers: mobx.IReactionDisposer[] = [];

    public constructor(
        meetingSaveDTO: IMeetingSaveDTO = defaultValues,
        meetingId: number = null,
        eventStartDateTime = null,
        params: IEventRequestParams = defaultParams,
        @IoC.Inject private readonly meetingsDAO: IMeetingsDAO = IoC.Container.get(IMeetingsDAO),
        @IoC.Inject private readonly globalViewModel: GlobalViewModel = IoC.Container.get(GlobalViewModel),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
    ) {
        this.params = params;
        this.meetingId = meetingId;
        this.room = meetingSaveDTO.room ? meetingSaveDTO.room : {
            roomId: null,
            roomName: '',
            roomLabel: ''
        };
        this.externalId = meetingSaveDTO.externalId || defaultValues.externalId;
        this.topic = meetingSaveDTO.topic || defaultValues.topic;
        this.status = meetingSaveDTO.status || defaultValues.status;
        this.type = meetingSaveDTO.type || defaultValues.type;
        this.track = meetingSaveDTO.track || defaultValues.track;
        this.notes = transpileInlineStylesToClasses(meetingSaveDTO.notes) || defaultValues.notes;
        this.passcode = meetingSaveDTO.passcode || defaultValues.passcode;
        this.path = meetingSaveDTO.path || defaultValues.path;
        this.meetingLink = meetingSaveDTO.meetingLink || defaultValues.meetingLink;
        this.meetingLinkWithEmbeddedBypassCode = meetingSaveDTO.meetingLinkWithEmbeddedBypassCode || defaultValues.meetingLink;
        this.access = meetingSaveDTO.meetingAccess || defaultValues.meetingAccess;
        const audienceCapacityEnabledFromApi = meetingSaveDTO.audienceCapacityEnabled;
        const audienceCapacityFromApi = meetingSaveDTO.audienceCapacity;
        const hasAudienceCapacityFromApi = audienceCapacityFromApi !== null && audienceCapacityFromApi !== undefined;
        const shouldDefaultBookableAudienceCapacity = !meetingId && this.access === Access.Bookable;
        this.isAudienceCapacityEnabled = audienceCapacityEnabledFromApi ?? (hasAudienceCapacityFromApi || shouldDefaultBookableAudienceCapacity);
        this.audienceCapacity = hasAudienceCapacityFromApi
            ? String(audienceCapacityFromApi)
            : shouldDefaultBookableAudienceCapacity ? '1' : '';
        this.dateTimeList = [{
            meetingDate: eventStartDateTime || defaultValues.startDateTime,
            startDate: defaultValues.startDateTime,
            endDate: defaultValues.endDateTime,
            meetingDatesChanged: false,
            startTimeValidationError: false,
            endTimeValidationError: false,
            isFormChecked: false,
        }];

        this.meetingDate = meetingSaveDTO.startDateTime || defaultValues.startDateTime;
        this.startDate = meetingSaveDTO.startDateTime || defaultValues.startDateTime;
        this.endDate = meetingSaveDTO.endDateTime || defaultValues.endDateTime;
        this.featured = meetingSaveDTO.featured || defaultValues.featured;
        this.replayEnabled = meetingSaveDTO.replayEnabled || defaultValues.replayEnabled;
        this.meetingRecordEnabled = meetingSaveDTO.meetingRecordEnabled || defaultValues.meetingRecordEnabled;
        this.links = meetingSaveDTO.links || defaultValues.links;
        this.addedLinks = [];
        const defaultConnecitonVersion = hasPermission(Permission.CREATE_MEETINGS_V2) ? ConnectionVersion.V2 : ConnectionVersion.V1;
        this.connectionVersion = this.isV2ConnectionVersion(meetingSaveDTO.connectionVersion || defaultConnecitonVersion);

        if (meetingSaveDTO.startDateTime && meetingSaveDTO.endDateTime) {
            const startDateTimeZoned = utcToZonedTime(meetingSaveDTO.startDateTime, this.params.timeZone);
            const endDateTimeZoned = utcToZonedTime(meetingSaveDTO.endDateTime, this.params.timeZone);
            this.showEndDate = !isSameDay(startDateTimeZoned, endDateTimeZoned);

            if (this.showEndDate) {
                this.meetingEndDate = meetingSaveDTO.endDateTime || defaultValues.endDateTime;
            }
        }

        this.uploadHeroImageS3VM = new UploadImageS3VM(this.params.accountId, this.params.eventId, 'heroImage', 'meeting');
        this.uploadThumbnailImageS3VM = new UploadImageS3VM(this.params.accountId, this.params.eventId, 'thumbnailImage', 'meeting');

        this.uploadHeroImageS3VM.addImage(
            meetingSaveDTO?.heroImageWithUrl?.assetUrl,
            meetingSaveDTO?.heroImage?.title,
            meetingSaveDTO?.heroImage?.objectKey
        );

        this.uploadThumbnailImageS3VM.addImage(
            meetingSaveDTO?.thumbnailImageWithUrl?.assetUrl,
            meetingSaveDTO?.thumbnailImage?.title,
            meetingSaveDTO?.thumbnailImage?.objectKey
        );

        this.templatesVM = new MeetingFormTemplatesVM(meetingSaveDTO.blueprintId, params.accountId);

        this.initMeetingExpiryDateVM(meetingSaveDTO.expiryDays);
        this.initMeetingExpiryDateVMReactions();

        this.initMeetingGoLiveDateVM(meetingSaveDTO.goLiveDateTime ?? null);
        this.initMeetingGoLiveDateVMReactions();
    }

    public async checkInitTemplatesVM(): Promise<void> {
        if (this.canApplyTemplateToMeeting() || this.shouldShowTemplateName()) {
            await this.templatesVM.initialize();
        }
    }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public canApplyTemplateToMeeting(): boolean {
        return (
            !this.meetingId &&
            hasPermission(Permission.BLUEPRINTS_TAB) &&
            hasPermission(Permission.CREATE_MEETING_FROM_TEMPLATE)
        );
    }

    public shouldShowTemplateName(): boolean {
        return (
            !!this.meetingId &&
            hasPermission(Permission.BLUEPRINTS_TAB) &&
            !!this.templatesVM.getTemplateId()
        );
    }

    public shouldShowTemplateLabel(): boolean {
        return this.shouldShowTemplateName() && !!this.getTemplateName();
    }

    public getSelectedTemplateId(): string {
        return this.templatesVM.getTemplateId() || '';
    }

    public get isLoadingTemplates(): boolean {
        return this.templatesVM.isLoading;
    }

    @mobx.action
    public selectTemplate(templateId: string): void {
        this.templatesVM.setTemplateId(templateId);
        this.applyTemplate();
    }

    public getTemplatesOptions(): { blueprintId: string; name: string }[] {
        const meetingVersion = this.connectionVersion ? ConnectionVersion.V2 : ConnectionVersion.V1;

        const filteredList = this.templatesVM.getTemplateList()
            .filter((template: IMeetingTemplateDTO) => {
                const templateVersion = template.connectionVersion ?? ConnectionVersion.V1;
                return templateVersion === meetingVersion;
            });

        return filteredList.map(({ blueprintId, name }) => ({ blueprintId: blueprintId, name: name }));
    }

    public getTemplateName(): string {
        return this.templatesVM.getTemplateName();
    }

    public templateHasConfigurationActions(): boolean {
        return this.templatesVM.templateHasActions();
    };

    public getDateTimeList(): IMeetingDateTime[] {
        return this.dateTimeList;
    }

    public getExternalIdValidationError(): boolean {
        return this.externalIdValidationError;
    }

    public getConnectionVersion = (): boolean => {
        return this.connectionVersion;
    };

    @mobx.action
    public setConnectionVersion = (value: boolean): boolean => {
        this.templatesVM.setTemplateId(null);
        return this.connectionVersion = value;
    };

    @mobx.action
    public setExternalIdValidationError(validationError: boolean): void {
        this.externalIdValidationError = validationError;
    }

    @mobx.action
    public removeValueFromDateTimeList = (index: number): void => {
        this.dateTimeList.splice(index, 1);
    };

    @mobx.action
    public setStartTimeInDateTimeList = (index: number, date: Date, eventTimezone: string, defaultMeetingDuration: number): void => {
        this.dateTimeList[index].startTimeValidationError = false;
        this.dateTimeList[index].meetingDate = this.getMeetingDateTime(date, eventTimezone, index);
        this.dateTimeList[index].startDate = this.getMeetingDateTime(date, eventTimezone, index);

        if (!this.dateTimeList[index].meetingDatesChanged && this.dateTimeList[index].endDate) {
            this.dateTimeList[index].endDate = this.getMeetingDateTime(this.dateTimeList[index].endDate, eventTimezone, index);
            this.dateTimeList[index].meetingDatesChanged = true;
        }
        if (defaultMeetingDuration && !this.dateTimeList[index].endDate) {
            const toBeEndTime = addMinutes(this.dateTimeList[index].startDate, defaultMeetingDuration);
            this.setEndTimeInDateTimeList(index, toBeEndTime, eventTimezone);
        }
    };

    @mobx.action
    public setMeetingDateInDateTimeList = (index: number, date: Date, eventTimezone: string): void => {
        this.dateTimeList[index].meetingDate = date;
        this.dateTimeList[index].endDate = this.dateTimeList[index].endDate
            && this.getMeetingDateTime(this.dateTimeList[index].endDate, eventTimezone, index);
        this.dateTimeList[index].startDate = this.dateTimeList[index].startDate
            && this.getMeetingDateTime(this.dateTimeList[index].startDate, eventTimezone, index);
        this.dateTimeList[index].meetingDatesChanged = true;
    };

    @mobx.action
    public setEndTimeInDateTimeList = (index: number, date: Date, eventTimezone: string): void => {
        this.dateTimeList[index].endTimeValidationError = false;
        this.dateTimeList[index].meetingDate = this.getMeetingDateTime(date, eventTimezone, index);
        this.dateTimeList[index].endDate = this.getMeetingDateTime(date, eventTimezone, index);
        this.dateTimeList[index].meetingDatesChanged = true;
    };

    public setPropertyInDateTimeList(index: number, name: string, date: Date, eventTimezone: string, defaultMeetingDuration: number): void {
        switch (name) {
            case 'meetingDate':
                this.setMeetingDateInDateTimeList(index, date, eventTimezone);
                break;
            case 'startDate':
                this.setStartTimeInDateTimeList(index, date, eventTimezone, defaultMeetingDuration);
                break;
            case 'endDate':
                this.setEndTimeInDateTimeList(index, date, eventTimezone);
                break;
        }
    }

    @mobx.action
    public setNewValueInDateTimeList = (index: number, name: string, date: Date, eventTimezone: string, defaultMeetingDuration?: number): void => {
        if (isValid(date)) {
            this.setPropertyInDateTimeList(index, name, date, eventTimezone, defaultMeetingDuration);
        }
        if (this.dateTimeList[index].startDate && this.dateTimeList[index].endDate) {
            this.dateTimeList[index].isFormChecked = true;
        }
    };

    @mobx.action
    public addNewValueInDateTimeList = (index: number): void => {
        // if add new empty row in dateTimeList form
        if (index === this.dateTimeList.length) {
            const item = {
                meetingDate: this.dateTimeList[index - 1]?.meetingDate ?? defaultValues.startDateTime,
                startDate: defaultValues.startDateTime,
                endDate: defaultValues.endDateTime,
                startTimeValidationError: false,
                endTimeValidationError: false,
                isFormChecked: false,
            };
            this.dateTimeList.push(item);
        }
    };

    public getTimeValidationErrorCreate(index: number): string | null {
        const startTimeValidationError = this.dateTimeList[index]?.startTimeValidationError;
        const endTimeValidationError = this.dateTimeList[index]?.endTimeValidationError;

        if (startTimeValidationError && endTimeValidationError) {
            return this.intl.formatMessage(TEXT_TIME_INVALID_ERROR_MESSAGE);
        }
        if (startTimeValidationError) {
            return this.intl.formatMessage(TEXT_START_TIME_INVALID_ERROR_MESSAGE);
        }
        if (endTimeValidationError) {
            return this.intl.formatMessage(TEXT_END_TIME_INVALID_ERROR_MESSAGE);
        }
        return null;
    }

    @mobx.action
    public setEndTimeValidationErrorCreate = (value: boolean, index: number): void => {
        this.dateTimeList[index].endTimeValidationError = value;
    };

    @mobx.action
    public setStartTimeValidationErrorCreate = (value: boolean, index: number): void => {
        this.dateTimeList[index].startTimeValidationError = value;
    };

    public getMeetingTimesErrorCreate(index: number): string {
        const startDate = this.dateTimeList[index]?.startDate;
        const endDate = this.dateTimeList[index]?.endDate;
        if ((!startDate || !endDate) && this.dateTimeList[index].isFormChecked) {
            return this.intl.formatMessage(TEXT_START_END_TIME_REQUIRED_ERROR_MESSAGE);
        }

        return (!isBefore(startDate, endDate) && this.dateTimeList[index].isFormChecked)
            ? this.intl.formatMessage(TEXT_START_END_TIME_EQUAL_GREATER_ERROR_MESSAGE)
            : null;
    }

    public getMeetingDateErrorCreate(index: number): string {
        return !this.dateTimeList[index]?.meetingDate ? this.intl.formatMessage(TEXT_DATE_REQUIRED_ERROR_MESSAGE) : null;
    }

    public getFieldLengthError(field: string, prefix = ''): string {
        return this[field]
            ? getShortTextLengthError(
                this.intl.formatMessage(
                    {
                        id: `oe.common.${field}`,
                        defaultMessage: `{prefix}${fieldNames[field]}`,
                        description: 'Label text',
                    },
                    {
                        prefix: prefix
                    }),
                this[field],
            ) : null;
    }

    public getRichTextFieldLengthError(field: string, prefix = ''): string {
        return this[field]
            ? getShortRichTextLengthError(
                this.intl.formatMessage(
                    {
                        id: `oe.common.${field}`,
                        defaultMessage: `{prefix}${fieldNames[field]}`,
                        description: 'Label text',
                    },
                    {
                        prefix: prefix
                    }),
                this[field],
            ) : null;
    }

    public getExternalIdValidationErrorMessage(): string {
        return this.getExternalIdValidationError() ? this.intl.formatMessage(TEXT_EXTERNAL_ID_ERROR_MESSAGE) : null;
    }

    public checkValidity(): boolean | string {
        const validationAdditionalFields = Object.keys(fieldNames).some(value => !!this.getFieldLengthError(value));
        const meetingNotesValidationError = !!this.getRichTextFieldLengthError('notes');
        const externalIdValidationError = this.getExternalIdValidationError();
        const audienceCapacityValidationError = !!this.getAudienceCapacityValidationError();
        if (!this.meetingId) {
            this.dateTimeList.map(value => value.isFormChecked = true);
            const meetingDatesError = this.dateTimeList.some((value, index) => !!this.getMeetingDateErrorCreate(index));
            const meetingTimesError = this.dateTimeList.some((value, index) => !!this.getMeetingTimesErrorCreate(index));
            return externalIdValidationError
                || meetingDatesError
                || meetingTimesError
                || validationAdditionalFields
                || meetingNotesValidationError
                || audienceCapacityValidationError;
        }

        this.isFormChecked = true;
        return this.getMeetingDateErrorEdit()
            || this.getMeetingEndDateErrorEdit()
            || this.getMeetingTimesErrorEdit()
            || this.getMeetingEndDateRequiredErrorEdit()
            || validationAdditionalFields
            || meetingNotesValidationError
            || externalIdValidationError
            || audienceCapacityValidationError
            || this.expiryVM.hasValidationError()
            || this.goLiveVM.hasValidationError();
    }

    public getMeetingId(): number {
        return this.meetingId;
    }

    public getExternalId(): string {
        return this.externalId;
    }
    @mobx.action
    public setStartTime = (date: Date, eventTimezone: string): void => {
        if (isValid(date)) {
            this.setStartTimeValidationErrorEdit(false);
            this.meetingDate = this.getMeetingDateTime(date, eventTimezone);
            this.startDate = this.getMeetingDateTime(date, eventTimezone);
            if (!this.showEndDate && !this.meetingDatesChanged) {
                this.endDate = this.getMeetingDateTime(this.endDate, eventTimezone);
                this.meetingDatesChanged = true;
                this.meetingStartTimeChanged = true;
            }
        }
    };

    @mobx.action
    public setEndTime = (date: Date, eventTimezone: string): void => {
        if (isValid(date)) {
            if (this.showEndDate && !this.sameStartEndDate && this.meetingEndDate) {
                this.meetingEndDate = this.getMeetingDateTime(date, eventTimezone, null, 'endDate');
                this.endDate = this.getMeetingDateTime(date, eventTimezone, null, 'endDate');
            } else {
                this.meetingDate = this.getMeetingDateTime(date, eventTimezone);
                this.endDate = this.getMeetingDateTime(date, eventTimezone);
            }
            this.setEndTimeValidationErrorEdit(false);
            this.meetingDatesChanged = true;
            this.meetingEndTimeChanged = true;
        }
    };

    @mobx.action
    public setMeetingDate = (date: Date, eventTimezone: string, dateType: string = 'startDate', sameStartEndDate = false): void => {
        if (isValid(date)) {
            this.sameStartEndDate = sameStartEndDate;
            if (this.showEndDate && !this.sameStartEndDate) {
                if (dateType === 'startDate') {
                    this.meetingDate = date;
                    this.startDate = this.startDate && this.getMeetingDateTime(this.startDate, eventTimezone);
                } else {
                    this.meetingEndDate = date;
                    this.endDate = this.endDate && this.getMeetingDateTime(this.endDate, eventTimezone, null, 'endDate');
                }
                this.meetingDatesChanged = true;
            } else {
                this.meetingDate = date;
                this.meetingEndDate = date;
                this.endDate = this.endDate && this.getMeetingDateTime(this.endDate, eventTimezone);
                this.startDate = this.startDate && this.getMeetingDateTime(this.startDate, eventTimezone);
                this.meetingDatesChanged = true;
            }
        }
    };

    @mobx.action
    public setExternalId(externalId: string): void {
        this.externalId = externalId;
    }

    public toAPIMeetingDTOCreate() {
        const meetingData = this.getMeetingDataWithoutDateTime();
        return this.dateTimeList.map(({ startDate, endDate }) => (
            {
                startDateTime: startDate,
                endDateTime: endDate,
                connectionVersion: this.getParsedConnectionVersion(),
                expiryDays: this.expiryVM.expiryDays,
                goLiveDateTime: this.goLiveVM.goLiveDateTime,
                ...meetingData,
            }));
    }

    public toAPIMeetingDTOEdit() {
        const meetingData = this.getMeetingDataWithoutDateTime();
        const thumbnailImage = this.uploadThumbnailImageS3VM.getImageToApi();
        const heroImage = this.uploadHeroImageS3VM.getImageToApi();

        return {
            ...meetingData,
            ...thumbnailImage,
            ...heroImage,
            startDateTime: this.startDate,
            endDateTime: this.endDate,
            connectionVersion: this.getParsedConnectionVersion(),
            expiryDays: this.expiryVM.expiryDays,
            goLiveDateTime: this.goLiveVM.goLiveDateTime,
        };
    }

    @mobx.action
    public setStartTimeValidationErrorEdit = (value: boolean) => {
        this.startTimeValidationError = value;
    };

    @mobx.action
    public setEndTimeValidationErrorEdit = (value: boolean) => {
        this.endTimeValidationError = value;
    };

    public getTimeValidationErrorEdit(): string | null {

        const startTimeValidationError = this.startTimeValidationError;
        const endTimeValidationError = this.endTimeValidationError;

        if (startTimeValidationError && endTimeValidationError) {
            return this.intl.formatMessage(TEXT_TIME_INVALID_ERROR_MESSAGE);
        }
        if (startTimeValidationError) {
            return this.intl.formatMessage(TEXT_START_TIME_INVALID_ERROR_MESSAGE);
        }
        if (endTimeValidationError) {
            return this.intl.formatMessage(TEXT_END_TIME_INVALID_ERROR_MESSAGE);
        }
        return null;
    }

    public getMeetingDateErrorEdit() {
        return !this.meetingDate ? this.intl.formatMessage(TEXT_DATE_REQUIRED_ERROR_MESSAGE) : null;
    }

    public getMeetingEndDateRequiredErrorEdit() {
        return this.showEndDate && !this.meetingEndDate ? this.intl.formatMessage(TEXT_END_DATE_REQUIRED_ERROR_MESSAGE) : null;
    }

    public getMeetingEndDateErrorEdit() {
        if (this.meetingDate && this.meetingEndDate) {
            const startDateTimeZoned = utcToZonedTime(this.meetingDate, this.params.timeZone);
            const endDateTimeZoned = utcToZonedTime(this.meetingEndDate, this.params.timeZone);

            if (this.showEndDate && !this.sameStartEndDate && this.meetingEndDate && !isBefore(startDateTimeZoned, endDateTimeZoned)) {
                if (!isSameDay(startDateTimeZoned, endDateTimeZoned))
                    return this.intl.formatMessage(TEXT_START_END_DATE_GREATER_ERROR_MESSAGE);
            }
        }
        return null;
    }

    public getMeetingTimesErrorEdit() {
        if (!this.startDate || !this.endDate) {
            return this.intl.formatMessage(TEXT_START_END_TIME_REQUIRED_ERROR_MESSAGE);
        }
        return !this.getMeetingEndDateErrorEdit()
            && !isBefore(this.startDate, this.endDate)
            && (this.showEndDate && this.meetingEndDate || !this.showEndDate)
            ? this.intl.formatMessage(TEXT_START_END_TIME_EQUAL_GREATER_ERROR_MESSAGE)
            : null;
    }

    public async handleSetAndCheckExternalId(externalId: string) {
        this.setExternalId(externalId);
        if (!!externalId) {
            const res = await this.getMeetingDetailsByExternalId();
            this.setExternalIdValidationError(res);
        } else {
            this.setExternalIdValidationError(false);
        }
    }

    public getParams(): IWithExternalIdEventRequestParams {
        return { ...this.params, externalId: this.getExternalId() };
    }

    public async getMeetingDetailsByExternalId() {
        try {
            const response = await this.meetingsDAO.getMeetingDetailsByExternalId(this.getParams());
            return response.meetingId !== this.meetingId ? true : false;
        } catch (e) {
            return false;
        }
    }

    @mobx.action
    public setShowEndDate(value: boolean): void {
        this.meetingEndDate = null;
        this.showEndDate = value;
    }

    public isImageUploading(): boolean {
        return this.uploadThumbnailImageS3VM.isImageUploading || this.uploadHeroImageS3VM.isImageUploading;
    }

    @mobx.action
    public handleChangeMeetingAccess = (e): void => {
        this.access = e.target.value as Access;
        if (this.access === Access.Closed || this.access === Access.Bookable) {
            this.replayEnabled = false;
        }
        if (!this.shouldShowAudienceCapacity()) {
            this.isAudienceCapacityEnabled = false;
            this.audienceCapacity = '';
            return;
        }
        if (this.access === Access.Bookable) {
            this.isAudienceCapacityEnabled = true;
            this.audienceCapacity = '1';
            return;
        }
        if (this.access === Access.Open) {
            this.isAudienceCapacityEnabled = false;
            this.audienceCapacity = '';
        }
    };

    @mobx.action
    public toggleAudienceCapacityEnabled = (): void => {
        this.isAudienceCapacityEnabled = !this.isAudienceCapacityEnabled;
        if (!this.isAudienceCapacityEnabled) {
            this.audienceCapacity = '';
            return;
        }
        if (!this.isAudienceCapacityValueValid()) {
            this.audienceCapacity = '1';
        }
    };

    @mobx.action
    public setAudienceCapacity = (value: string): void => {
        this.audienceCapacity = value;
    };

    public shouldShowAudienceCapacity = (): boolean => {
        return this.access === Access.Open || this.access === Access.Bookable;
    };

    public getAudienceCapacityValidationError = (): string | null => {
        if (!this.shouldShowAudienceCapacity() || !this.isAudienceCapacityEnabled) {
            return null;
        }
        if (!this.isAudienceCapacityValueValid()) {
            return this.intl.formatMessage(TEXT_AUDIENCE_CAPACITY_RANGE_ERROR_MESSAGE);
        }
        return null;
    };

    @mobx.action
    public toggleFeaturedMeeting = (): void => {
        this.featured = !this.featured;
    };

    @mobx.action
    public toggleReplayEnabledMeeting = (): void => {
        this.replayEnabled = !this.replayEnabled;
    };

    @mobx.action
    public setAddedLinks = (link: ILinkDTO): void => {
        this.addedLinks.push(link);
    };

    @mobx.action
    public removeAddedLinks = (link: ILinkDTO): void => {
        const index = this.addedLinks.findIndex(addedLink => (addedLink.url === link.url) && (addedLink.label === link.label));
        if (index > -1) this.addedLinks.splice(index, 1);
    };

    @mobx.action
    public getAddedLinks = (): ILinkDTO[] => {
        return this.addedLinks;
    };

    @mobx.action
    public toggleMeetingRecordeEnabled = (): void => {
        this.meetingRecordEnabled = !this.meetingRecordEnabled;
    };

    public getSelectedMeetingTemplateDetails(): IBlueprintItemDetails | null {
        const selectedMeeting = this.templatesVM.getSelectTemplate();

        return selectedMeeting
            ? convertBlueprintTemplateIntoBlueprintDetails(
                selectedMeeting.type,
                selectedMeeting,
                this.globalViewModel.getTimezonesVM().getTimezonesList() || [],
                this.templatesVM.getTemplateList(),
                this.templatesVM.getConnectionTemplatesList(),
            )
            : null;
    }

    public getSelectedMeetingType(): BlueprintType {
        return this.getSelectedTemplateId() && this.templatesVM.getSelectTemplate().type;
    }

    public getParsedConnectionVersion = (): ConnectionVersion => {
        return this.connectionVersion ? ConnectionVersion.V2 : ConnectionVersion.V1;
    };

    public disposeReactions = (): void => {
        this.disposeExpiryVMReactions();
        this.disposeGoLiveVMReactions();
    };

    @mobx.action
    private applyTemplate(): void {
        const values = this.templatesVM.getTemplateFieldsValues();

        if (values.status) this.status = values.status as MeetingStatus;
        if (values.meetingAccess) this.access = values.meetingAccess as Access;

        if (values.replayEnabled && values.meetingAccess && values.meetingAccess !== Access.Closed && values.meetingAccess !== Access.Bookable) {
            this.replayEnabled = values.replayEnabled as boolean;
        }

        if (values.featured) this.featured = values.featured as boolean;
        if (values.type) this.type = values.type as string;
        if (values.track) this.track = values.track as string;
        if (values.topic) this.topic = values.topic as string;

        if ((values.links as MeetingTemplateLink[])?.length) {
            const meetingLinks = (values.links as MeetingTemplateLink[]).map(link => ({ ...link, linkId: null }));

            this.addedLinks.splice(0, this.addedLinks.length, ...meetingLinks);
        }

        if (values.notes) this.notes = transpileInlineStylesToClasses(values.notes as string);
        this.meetingRecordEnabled = values.recordEnabled as boolean;

        if (values.expiryDays) this.initMeetingExpiryDateVM(values.expiryDays as number);
    }

    private getMeetingDataWithoutDateTime() {
        return {
            room: this.room.roomId ? this.room : null,
            externalId: this.externalId?.trim() || null,
            topic: this.topic,
            status: this.status,
            notes: transpileClassesToInlineStyles(this.notes),
            type: this.type,
            meetingAccess: this.access,
            audienceCapacityEnabled: this.shouldShowAudienceCapacity() ? this.isAudienceCapacityEnabled : false,
            audienceCapacity: this.getAudienceCapacityForPayload(),
            track: this.track?.trim() || null,
            blueprintId: this.templatesVM.getTemplateId()
        };
    }

    private isAudienceCapacityValueValid = (): boolean => {
        if (!this.audienceCapacity.trim()) {
            return false;
        }
        const parsedAudienceCapacity = Number(this.audienceCapacity);
        return Number.isInteger(parsedAudienceCapacity) && parsedAudienceCapacity >= 1 && parsedAudienceCapacity <= 99999;
    };

    private getAudienceCapacityForPayload = (): number | null => {
        if (!this.shouldShowAudienceCapacity() || !this.isAudienceCapacityEnabled || !this.isAudienceCapacityValueValid()) {
            return null;
        }
        return Number(this.audienceCapacity);
    };

    private getMeetingDateTime(date: Date, timeZone: string, index: number = null, dateType: string = 'startDate') {
        let currentMeetingDate = null;
        if (dateType === 'startDate') {
            currentMeetingDate = !this.meetingId ? this.dateTimeList[index]?.meetingDate : this.meetingDate;
        } else {
            currentMeetingDate = this.meetingEndDate;
        }
        const meetingDate = format(utcToZonedTime(currentMeetingDate, timeZone), DateFnsFormats.FullDate).slice(0, 10);
        const meetingTime = format(utcToZonedTime(date, timeZone), TimeFormats.HoursMinutesSeconds);
        return zonedTimeToUtc(`${meetingDate} ${meetingTime}`, timeZone);
    }

    private isV2ConnectionVersion = (connectionVersion: ConnectionVersion): boolean => {
        return connectionVersion === ConnectionVersion.V2;
    };

    private initMeetingExpiryDateVM = (expiryDays: number | null): void => {
        this.expiryVM = new MeetingExpiryDateVM(expiryDays);
        this.expiryVM.setMeetingDate(this.meetingDate);
        this.expiryVM.setStartDate(this.startDate);
        this.expiryVM.setTimezone(this.params.timeZone);
    };

    private initMeetingExpiryDateVMReactions = (): void => {
        this.expiryVMReactionDisposers.push(
            mobx.reaction(
                () => this.meetingDate,
                meetingDate => this.expiryVM.setMeetingDate(meetingDate)
            )
        );

        this.expiryVMReactionDisposers.push(
            mobx.reaction(
                () => this.startDate,
                startDate => this.expiryVM.setStartDate(startDate)
            )
        );

        this.expiryVMReactionDisposers.push(
            mobx.reaction(
                () => this.params.timeZone,
                timezone => this.expiryVM.setTimezone(timezone)
            )
        );
    };

    private disposeExpiryVMReactions = (): void => {
        this.expiryVMReactionDisposers.forEach(disposer => disposer());
        this.expiryVMReactionDisposers = [];
    };

    private initMeetingGoLiveDateVM = (goLiveDateTime: Date | null): void => {
        this.goLiveVM = new MeetingGoLiveDateVM(goLiveDateTime);
        this.goLiveVM.setMeetingDate(this.meetingDate);
        this.goLiveVM.setStartDate(this.startDate);
        this.goLiveVM.setTimezone(this.params.timeZone);
    };

    private initMeetingGoLiveDateVMReactions = (): void => {
        this.goLiveVMReactionDisposers.push(
            mobx.reaction(
                () => this.meetingDate,
                meetingDate => this.goLiveVM.setMeetingDate(meetingDate)
            )
        );

        this.goLiveVMReactionDisposers.push(
            mobx.reaction(
                () => this.startDate,
                startDate => this.goLiveVM.setStartDate(startDate)
            )
        );

        this.goLiveVMReactionDisposers.push(
            mobx.reaction(
                () => this.params.timeZone,
                timezone => this.goLiveVM.setTimezone(timezone)
            )
        );
    };

    private disposeGoLiveVMReactions = (): void => {
        this.goLiveVMReactionDisposers.forEach(disposer => disposer());
        this.goLiveVMReactionDisposers = [];
    };
}

export { MeetingFormDataVM };

